import { useMemo, useState } from 'react';
import { T, useIsDesktop, Bone } from './reportTokens';
import SectionError from './SectionError';
import SectionEmpty from './SectionEmpty';
import KpiTooltip from '../KpiTooltip';
import { getCurrencySymbol, getCurrencySymbolForCountry } from '../../utils/currency';
import { isNativePlatform, exportTableXlsxNative, exportTablePdfNative } from '../../utils/capacitorExport';
import { escapeCsvCell, CSV_BOM, exportedOnLabel, generatedAtLabel, type ExportFormat } from '../../utils/reportExport';
import { useAuth } from '../../context/AuthContext';
import { useOutletAccess, type AccessibleOutlet } from '../../hooks/useOutletAccess';
import { getSalesByChannelBreakdownApi, type OrderSource } from '../../redux/reports/clickHouse/clickHouseApi';
import { ReportExportTriggerButton, ReportExportOptionsMenu } from './components/ReportExportButton';
import { ExportScopeModal, type ExportScope } from './components/ExportScopeModal';
import type { OrderChannelGroup, SalesByChannelBreakdownResponse, SalesByChannelBreakdownRow } from '../../interface/clickHouseInterface';

/* ══════════════════════════════════════════════════════════════════════════
   Sales by Channel — Financial Breakdown  (ZAR-1555)

   ONE shared component rendered identically on the Sales Report and the EOD
   Reconciliation report. Desktop → table; mobile → expandable cards + pinned
   grand-total bar.

   Data source: GET /sales/live/v2/sales-by-channel-breakdown (ClickHouse),
   fetched through the clickHouse Redux slice and shaped for the render layer
   by `mapSalesByChannelBreakdown()` below. Screens pass `data` + `totals`.
   ══════════════════════════════════════════════════════════════════════════ */

// ─── Types ───────────────────────────────────────────────────────────────
export type ChannelKind = 'firstParty' | 'marketplace';

/** A money cell is `null` when the platform does not report it (renders as —). */
export interface FeeCells {
    itemTotal: number | null;
    discount: number | null;
    deliveryFee: number | null;
    convenienceFee: number | null;
    serviceFee: number | null;
    cardProcessingFee: number | null;
    tips: number | null; // restaurant-retained tips ONLY (driver tips excluded)
    tax: number | null;
}

export interface FulfillmentSubRow {
    label: string; // 'Delivery' | 'Pickup' | 'Dine-In' | 'In-Store'
    orders: number;
    cells: FeeCells;
    /** Backend-reported Total Collected. Falls back to the computed identity when absent. */
    total?: number;
}

export interface ChannelRow {
    id: string;
    label: string;
    /** Raw API `channelGroup` — what the Order Log drill-down filters on. */
    channelGroup?: OrderChannelGroup;
    kind: ChannelKind;
    orders: number;
    cells: FeeCells;
    /** Backend-reported Total Collected (sum of this channel's fulfillment rows). */
    total?: number;
    /** Marketplace item totals are net of platform promotions → show an asterisk. */
    itemTotalNet?: boolean;
    /** Marketplace facilitator (remitted) tax — rendered in blue with an RMT badge. */
    taxRemitted?: boolean;
    subRows?: FulfillmentSubRow[];
}

export interface ChannelGroup {
    id: string;
    label: string; // 'Direct Online' | 'Marketplace' | 'In-Store · POS'
    channels: ChannelRow[];
    /** Always render even with zero orders (In-Store · POS). */
    alwaysRender?: boolean;
    /**
     * When the group holds exactly one channel AND the channel *is* the group
     * (Direct Online, In-Store · POS), collapse to a single row with no group
     * header / subtotal. Marketplace never collapses — the platform name matters.
     */
    collapseSingle?: boolean;
}

/** Grand-total row straight from the API `totals` object. */
export interface ChannelGrandTotals {
    orders: number;
    cells: FeeCells;
    total: number;
}

// ─── Money-column config (order is the on-screen / export column order) ────
type MoneyKey = keyof FeeCells;
interface MoneyCol {
    key: MoneyKey;
    label: string;
    /** US outlets label this column differently (see `colLabel`). */
    usLabel?: string;
    discount?: boolean;
    tips?: boolean;
    tax?: boolean;
    total?: boolean;
}
const MONEY_COLS: MoneyCol[] = [
    { key: 'itemTotal', label: 'Item Total' },
    { key: 'discount', label: 'Discount', discount: true },
    { key: 'deliveryFee', label: 'Delivery Fee' },
    { key: 'convenienceFee', label: 'Convenience Fee' },
    { key: 'serviceFee', label: 'Service Fee', usLabel: 'Gratuity' },
    { key: 'cardProcessingFee', label: 'Card Processing Fee' },
    { key: 'tips', label: 'Tips', tips: true },
    { key: 'tax', label: 'Tax', tax: true },
];

/**
 * Column heading for the active outlet's country. US outlets call the service
 * fee "Gratuity". Read at render / export time so it follows the outlet switch,
 * and applied everywhere the header appears — table, mobile cards, CSV, xlsx, PDF.
 */
function colLabel(col: MoneyCol): string {
    // This branch's currency.ts tracks only the resolved symbol, not a country
    // code; by its own rule (IN -> Rs, US -> $, else -> '') '$' only ever comes
    // from a US outlet, so it doubles as the US check here.
    return getCurrencySymbol() === '$' && col.usLabel ? col.usLabel : col.label;
}

const ASTERISK_TOOLTIP =
    'Net of any platform-applied promotion. Promotion data is not reported by the platform, so discounts on these orders cannot be separated.';
const TIPS_TOOLTIP =
    'Restaurant-retained tips only. Driver tips on marketplace orders are excluded — not shown, not summed, not exported.';
const GRAND_TOTAL_TOOLTIP =
    'Total amount paid by guests across all channels for the period. This is NOT the operator’s expected deposit — marketplace fees and commission are retained by the platform.';

// ─── Identity: Total Collected = sum of populated money components ─────────
// Fallback only — used when the backend does not report `total` for a row.
// null is treated as ABSENT, not zero.
const g = (v: number | null | undefined): number => v ?? 0;
export function totalCollected(cells: FeeCells, kind: ChannelKind): number {
    if (kind === 'marketplace') {
        // Item total + Delivery + Convenience + Card processing + Tips + Tax (no discount, no service fee)
        return g(cells.itemTotal) + g(cells.deliveryFee) + g(cells.convenienceFee) + g(cells.cardProcessingFee) + g(cells.tips) + g(cells.tax);
    }
    // First party: Item total - Discount + Delivery + Convenience + Service + Card processing + Tips + Tax
    return g(cells.itemTotal) - g(cells.discount) + g(cells.deliveryFee) + g(cells.convenienceFee) + g(cells.serviceFee) + g(cells.cardProcessingFee) + g(cells.tips) + g(cells.tax);
}

/** Backend-reported total when present, otherwise the computed identity. */
function rowTotal(row: { cells: FeeCells; total?: number }, kind: ChannelKind): number {
    return row.total ?? totalCollected(row.cells, kind);
}

// Sum a money column across rows, returning null when NO row reports it
// (so a fully-unreported column stays "—" instead of collapsing to 0).
function sumCol(rows: FeeCells[], key: MoneyKey): number | null {
    let any = false;
    let sum = 0;
    for (const r of rows) {
        if (r[key] != null) { any = true; sum += r[key] as number; }
    }
    return any ? sum : null;
}

function aggregateCells(rows: FeeCells[]): FeeCells {
    return {
        itemTotal: sumCol(rows, 'itemTotal'),
        discount: sumCol(rows, 'discount'),
        deliveryFee: sumCol(rows, 'deliveryFee'),
        convenienceFee: sumCol(rows, 'convenienceFee'),
        serviceFee: sumCol(rows, 'serviceFee'),
        cardProcessingFee: sumCol(rows, 'cardProcessingFee'),
        tips: sumCol(rows, 'tips'),
        tax: sumCol(rows, 'tax'),
    };
}

// ═══════════════════════════════════════════════════════════════════════════
//  API → view model
//  GET /sales/live/v2/sales-by-channel-breakdown
// ═══════════════════════════════════════════════════════════════════════════

/** channelGroup values that are first-party; everything else is a marketplace. */
const FIRST_PARTY_GROUPS: Record<string, { id: string; label: string; alwaysRender?: boolean }> = {
    DIRECT_ONLINE: { id: 'direct-online', label: 'Direct Online' },
    IN_STORE: { id: 'in-store-pos', label: 'In-Store · POS', alwaysRender: true },
};

/**
 * Marketplace fulfillment labels. Third-party platforms report their pickup
 * orders as "In-Store" — surface them as Pickup, which is what they are.
 */
function marketplaceChannelLabel(channel: string): string {
    return channel.trim().toLowerCase() === 'in-store' ? 'Pickup' : channel;
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);

function cellsFromRow(r: SalesByChannelBreakdownRow): FeeCells {
    return {
        itemTotal: num(r.itemTotal),
        discount: num(r.discount),
        deliveryFee: num(r.deliveryFee),
        convenienceFee: num(r.convenienceFee),
        serviceFee: num(r.serviceFee),
        cardProcessingFee: num(r.cardProcessingFee),
        tips: num(r.tips),
        tax: num(r.tax),
    };
}

/** Merge fulfillment rows that collapse onto the same label (e.g. In-Store → Pickup). */
function mergeSubRows(subRows: FulfillmentSubRow[]): FulfillmentSubRow[] {
    const byLabel = new Map<string, FulfillmentSubRow>();
    for (const sub of subRows) {
        const existing = byLabel.get(sub.label);
        if (!existing) { byLabel.set(sub.label, { ...sub }); continue; }
        existing.orders += sub.orders;
        existing.cells = aggregateCells([existing.cells, sub.cells]);
        existing.total = g(existing.total) + g(sub.total);
    }
    return [...byLabel.values()];
}

function toChannelRow(id: string, label: string, channelGroup: OrderChannelGroup, kind: ChannelKind, apiRows: SalesByChannelBreakdownRow[]): ChannelRow {
    const isMarketplace = kind === 'marketplace';
    const subRows = mergeSubRows(apiRows.map((r) => ({
        label: isMarketplace ? marketplaceChannelLabel(r.channel ?? '') : (r.channel ?? ''),
        orders: num(r.orderCount),
        cells: cellsFromRow(r),
        total: num(r.totalCollected),
    })));
    // Parent row = sum of its fulfillment rows.
    return {
        id,
        label,
        channelGroup,
        kind,
        orders: subRows.reduce((s, r) => s + r.orders, 0),
        cells: aggregateCells(subRows.map((r) => r.cells)),
        total: subRows.reduce((s, r) => s + g(r.total), 0),
        itemTotalNet: isMarketplace || undefined,
        subRows: subRows.length ? subRows : undefined,
    };
}

/**
 * Shape the API response into the `ChannelGroup[]` the render layer consumes.
 * Groups always come out in design order: Direct Online → Marketplace → In-Store · POS.
 */
export function mapSalesByChannelBreakdown(res: SalesByChannelBreakdownResponse | null | undefined): {
    groups: ChannelGroup[];
    totals?: ChannelGrandTotals;
} {
    const rows = Array.isArray(res?.rows) ? res!.rows : [];
    if (!rows.length) return { groups: [] };

    // Bucket rows by channelGroup, preserving first-seen order for marketplaces.
    const byGroup = new Map<string, SalesByChannelBreakdownRow[]>();
    for (const r of rows) {
        const key = (r.channelGroup ?? '').trim();
        if (!key) continue;
        const bucket = byGroup.get(key);
        if (bucket) bucket.push(r); else byGroup.set(key, [r]);
    }

    const groups: ChannelGroup[] = [];

    const pushFirstParty = (groupKey: string) => {
        const apiRows = byGroup.get(groupKey);
        if (!apiRows?.length) return;
        const meta = FIRST_PARTY_GROUPS[groupKey];
        groups.push({
            id: meta.id,
            label: meta.label,
            alwaysRender: meta.alwaysRender,
            collapseSingle: true,
            channels: [toChannelRow(meta.id, meta.label, groupKey as OrderChannelGroup, 'firstParty', apiRows)],
        });
    };

    pushFirstParty('DIRECT_ONLINE');

    // Everything that is not a known first-party group is a third-party marketplace.
    const marketplaceChannels: ChannelRow[] = [];
    byGroup.forEach((apiRows, groupKey) => {
        if (FIRST_PARTY_GROUPS[groupKey]) return;
        marketplaceChannels.push(toChannelRow(`marketplace-${groupKey}`, groupKey, groupKey as OrderChannelGroup, 'marketplace', apiRows));
    });
    if (marketplaceChannels.length) {
        groups.push({ id: 'marketplace', label: 'Marketplace', channels: marketplaceChannels });
    }

    pushFirstParty('IN_STORE');

    const t = res?.totals;
    return {
        groups,
        totals: t ? { orders: num(t.orderCount), cells: cellsFromRow(t), total: num(t.totalCollected) } : undefined,
    };
}

// ─── Formatting ───────────────────────────────────────────────────────────
function useMoney() {
    const sym = getCurrencySymbol();
    return useMemo(() => ({
        sym,
        money: (v: number | null): string =>
            v == null ? '—' : `${sym}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        num: (v: number): string => v.toLocaleString('en-US'),
    }), [sym]);
}

/**
 * Plain (no-hook) money formatter for the export builders. Takes the symbol
 * explicitly rather than reading the active one, because the all-outlets export
 * formats each outlet's figures in that outlet's own currency.
 */
function moneyWithSymbol(v: number | null, sym: string, grouped = true): string {
    if (v == null) return '—';
    const n = Number(v).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: grouped,
    });
    return `${sym}${n}`;
}

// ─── Derived aggregate model shared by all render paths ────────────────────
interface DerivedGroup {
    group: ChannelGroup;
    single: boolean; // one channel that *is* the group → no header / no subtotal row
    visibleChannels: ChannelRow[];
    subtotalCells: FeeCells;
    subtotalOrders: number;
    subtotalTotal: number;
    subtotalHasRmt: boolean;
}
interface Derived {
    groups: DerivedGroup[];
    grandCells: FeeCells;
    grandOrders: number;
    grandTotal: number;
    /** Any channel reports marketplace-facilitator (remitted) tax → show the rmt/op split. */
    hasRmt: boolean;
    grandTaxRmt: number; // remitted (marketplace facilitator) portion
    grandTaxOp: number;  // operator-remitted portion
    hasNet: boolean;     // any channel carries the net-of-promotion asterisk
    isEmpty: boolean;
}

/**
 * Pure form of {@link useDerived}. Split out so the all-outlets export can derive
 * a model for each outlet it fetches, where hooks are not available.
 */
function deriveModel(data: ChannelGroup[], totals?: ChannelGrandTotals): Derived {
    const groups: DerivedGroup[] = [];
    const allRows: FeeCells[] = [];
    const allChannels: ChannelRow[] = [];

    for (const group of data) {
        // Zero-order channels are omitted, except an always-render group (In-Store · POS).
        const visibleChannels = group.channels.filter((c) => c.orders > 0 || group.alwaysRender);
        if (visibleChannels.length === 0 && !group.alwaysRender) continue;

        const cells = visibleChannels.map((c) => c.cells);
        const subtotalCells = aggregateCells(cells);
        const subtotalOrders = visibleChannels.reduce((s, c) => s + c.orders, 0);
        const subtotalTotal = visibleChannels.reduce((s, c) => s + rowTotal(c, c.kind), 0);
        const subtotalHasRmt = visibleChannels.some((c) => c.taxRemitted && c.cells.tax != null);

        groups.push({
            group,
            single: !!group.collapseSingle && visibleChannels.length === 1,
            visibleChannels,
            subtotalCells,
            subtotalOrders,
            subtotalTotal,
            subtotalHasRmt,
        });
        allRows.push(...cells);
        allChannels.push(...visibleChannels);
    }

    // Grand total comes from the API `totals` object when supplied; the
    // aggregate is the fallback so the mock / partial payloads still render.
    const grandCells = totals?.cells ?? aggregateCells(allRows);
    const grandOrders = totals?.orders ?? allChannels.reduce((s, c) => s + c.orders, 0);
    const grandTotal = totals?.total ?? allChannels.reduce((s, c) => s + rowTotal(c, c.kind), 0);
    const grandTaxRmt = allChannels.reduce((s, c) => s + (c.taxRemitted && c.cells.tax != null ? c.cells.tax : 0), 0);

    return {
        groups,
        grandCells,
        grandOrders,
        grandTotal,
        hasRmt: allChannels.some((c) => c.taxRemitted && c.cells.tax != null),
        grandTaxRmt,
        grandTaxOp: g(grandCells.tax) - grandTaxRmt,
        hasNet: allChannels.some((c) => c.itemTotalNet),
        isEmpty: groups.length === 0,
    };
}

function useDerived(data: ChannelGroup[], totals?: ChannelGrandTotals): Derived {
    return useMemo(() => deriveModel(data, totals), [data, totals]);
}

// ─── Small shared UI atoms ─────────────────────────────────────────────────
function Caret({ open }: { open: boolean }) {
    return (
        <span style={{ display: 'inline-block', width: 12, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none', color: T.t2, fontSize: 10, lineHeight: 1 }}>
            {'▶'}
        </span>
    );
}

function RmtBadge() {
    return (
        <span style={{ marginLeft: 5, fontSize: 8, fontWeight: 700, letterSpacing: 0.4, color: T.blueDark, background: T.blueDarkSoft, padding: '1px 4px', borderRadius: 4, verticalAlign: 'middle' }}>
            RMT
        </span>
    );
}

// Superscript asterisk with a hover/tap tooltip (net-of-promotion note).
function NetAsterisk() {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const show = (e: React.MouseEvent) => {
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setPos({ top: r.top - 8, left: Math.min(r.left, window.innerWidth - 260) });
        setOpen(true);
    };
    return (
        <sup
            onMouseEnter={show}
            onMouseLeave={() => setOpen(false)}
            onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : show(e); }}
            style={{ marginLeft: 2, color: T.orange, fontWeight: 700, cursor: 'help', position: 'relative', top: -1 }}
        >
            *
            {open && (
                <span style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateY(-100%)', width: 240, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '8px 10px', fontSize: 11, fontWeight: 400, color: T.t2, lineHeight: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', zIndex: 9999, textTransform: 'none', letterSpacing: 0, whiteSpace: 'normal', textAlign: 'left' }}>
                    {ASTERISK_TOOLTIP}
                </span>
            )}
        </sup>
    );
}

// Render a money value with tax (RMT) / discount colouring and the net asterisk.
function MoneyValue({ col, value, itemNet, taxRmt, dark }: { col: MoneyCol; value: number | null; itemNet?: boolean; taxRmt?: boolean; dark?: boolean }) {
    const { money } = useMoney();
    const isItem = col.key === 'itemTotal';
    const showAsterisk = isItem && itemNet && value != null;
    const showRmt = col.tax && taxRmt && value != null;
    let color: string | undefined;
    if (dark) color = '#fff';
    else if (col.discount && value != null) color = T.accent;
    else if (showRmt) color = T.blueDark;
    return (
        <span style={{ color, whiteSpace: 'nowrap', textDecoration: showRmt ? 'underline dotted' : undefined, textUnderlineOffset: 3 }}>
            {money(value)}
            {showAsterisk && <NetAsterisk />}
            {showRmt && <RmtBadge />}
        </span>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
//  DESKTOP TABLE
// ═══════════════════════════════════════════════════════════════════════════
function DesktopTable({ derived, onChannelDrill }: { derived: Derived; onChannelDrill?: (channel: ChannelRow) => void }) {
    const { money, num: fmtNum } = useMoney();
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const toggle = (id: string) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const th: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { fontSize: 13, color: T.t1, padding: '13px 14px', textAlign: 'right', whiteSpace: 'nowrap' };
    const nameLink = !!onChannelDrill;

    const ChannelCell = ({ row, label, indent = 0 }: { row?: ChannelRow; label: string; indent?: number }) => {
        const canExpand = !!row?.subRows?.length;
        const open = row ? expanded.has(row.id) : false;
        return (
            <td style={{ ...td, textAlign: 'left', paddingLeft: 14 + indent }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {canExpand ? (
                        <span onClick={() => row && toggle(row.id)} style={{ cursor: 'pointer', display: 'inline-flex' }}><Caret open={open} /></span>
                    ) : <span style={{ width: 12 }} />}
                    <span
                        onClick={row && nameLink ? () => onChannelDrill!(row) : undefined}
                        style={{ fontWeight: 600, color: row && nameLink ? T.accent : T.t1, cursor: row && nameLink ? 'pointer' : 'default' }}
                    >
                        {label}{row && nameLink ? ' ›' : ''}
                    </span>
                </span>
            </td>
        );
    };

    const moneyCells = (cells: FeeCells, opts: { itemNet?: boolean; taxRmt?: boolean; dim?: boolean; bold?: boolean } = {}) =>
        MONEY_COLS.map((col) => (
            <td key={col.key} style={{ ...td, color: opts.dim ? T.t3 : undefined, fontWeight: opts.bold ? 700 : 400 }}>
                <MoneyValue col={col} value={cells[col.key]} itemNet={opts.itemNet} taxRmt={opts.taxRmt} />
            </td>
        ));

    return (
        <div style={{ overflowX: 'auto', border: `1px solid ${T.cardBorder}`, borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: T.font, minWidth: 1060 }}>
                <thead>
                    <tr style={{ borderBottom: `1px solid ${T.divider}` }}>
                        <th style={{ ...th, textAlign: 'left' }}>Channel</th>
                        <th style={th}>Orders</th>
                        {MONEY_COLS.map((col) => (
                            <th key={col.key} style={th}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                    {colLabel(col)}
                                    {col.tips && <KpiTooltip text={TIPS_TOOLTIP} />}
                                </span>
                            </th>
                        ))}
                        <th style={th}>Total Collected</th>
                    </tr>
                </thead>
                <tbody>
                    {derived.groups.map((dg) => (
                        <GroupBody key={dg.group.id} dg={dg} />
                    ))}
                </tbody>
                <tfoot>
                    <tr style={{ background: T.t1, color: '#fff' }}>
                        <td style={{ ...td, color: '#fff', fontWeight: 700 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                Grand Total <KpiTooltip text={GRAND_TOTAL_TOOLTIP} />
                            </span>
                        </td>
                        <td style={{ ...td, color: '#fff', fontWeight: 700 }}>{fmtNum(derived.grandOrders)}</td>
                        {MONEY_COLS.map((col) => (
                            <td key={col.key} style={{ ...td, color: '#fff', fontWeight: 700 }}>
                                {col.tax && derived.hasRmt ? (
                                    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
                                        <span>{money(derived.grandCells.tax)}</span>
                                        <span style={{ fontSize: 9, fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
                                            rmt {money(derived.grandTaxRmt)} · op {money(derived.grandTaxOp)}
                                        </span>
                                    </span>
                                ) : (
                                    <MoneyValue col={col} value={derived.grandCells[col.key]} dark />
                                )}
                            </td>
                        ))}
                        <td style={{ ...td, color: '#fff', fontWeight: 800, fontSize: 14 }}>{money(derived.grandTotal)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );

    // Group body (nested so it can read expanded/toggle closures)
    function GroupBody({ dg }: { dg: DerivedGroup }) {
        const rows: React.ReactNode[] = [];
        if (!dg.single) {
            rows.push(
                <tr key={`${dg.group.id}-hdr`}>
                    <td colSpan={MONEY_COLS.length + 3} style={{ padding: '10px 14px 4px', fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: T.t3, textTransform: 'uppercase' }}>
                        {dg.group.label}
                    </td>
                </tr>,
            );
        }
        dg.visibleChannels.forEach((row) => {
            const open = expanded.has(row.id);
            const label = dg.single ? dg.group.label : row.label;
            rows.push(
                <tr key={row.id} style={{ borderTop: `1px solid ${T.divider}` }}>
                    <ChannelCell row={row} label={label} />
                    <td style={td}>{fmtNum(row.orders)}</td>
                    {moneyCells(row.cells, { itemNet: row.itemTotalNet, taxRmt: row.taxRemitted })}
                    <td style={{ ...td, fontWeight: 700 }}>{money(rowTotal(row, row.kind))}</td>
                </tr>,
            );
            if (open && row.subRows) {
                row.subRows.forEach((sub, i) => {
                    rows.push(
                        <tr key={`${row.id}-sub-${i}`} style={{ background: T.bg }}>
                            <ChannelCell label={sub.label} indent={22} />
                            <td style={{ ...td, color: T.t3 }}>{fmtNum(sub.orders)}</td>
                            {moneyCells(sub.cells, { itemNet: row.itemTotalNet, taxRmt: row.taxRemitted, dim: true })}
                            <td style={{ ...td, color: T.t2, fontWeight: 600 }}>{money(rowTotal(sub, row.kind))}</td>
                        </tr>,
                    );
                });
            }
        });
        if (!dg.single) {
            rows.push(
                <tr key={`${dg.group.id}-sub`} style={{ borderTop: `1px solid ${T.cardBorder}`, background: T.accentSoft }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{dg.group.label} subtotal</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmtNum(dg.subtotalOrders)}</td>
                    {moneyCells(dg.subtotalCells, { itemNet: dg.visibleChannels.some((c) => c.itemTotalNet), taxRmt: dg.subtotalHasRmt, bold: true })}
                    <td style={{ ...td, fontWeight: 800 }}>{money(dg.subtotalTotal)}</td>
                </tr>,
            );
        }
        return <>{rows}</>;
    }
}

// ═══════════════════════════════════════════════════════════════════════════
//  MOBILE CARDS
// ═══════════════════════════════════════════════════════════════════════════
function MobileCards({ derived, onChannelDrill }: { derived: Derived; onChannelDrill?: (channel: ChannelRow) => void }) {
    const { money, num: fmtNum } = useMoney();
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const toggle = (id: string) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const FeeLine = ({ col, value, itemNet, taxRmt }: { col: MoneyCol; value: number | null; itemNet?: boolean; taxRmt?: boolean }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: 12.5 }}>
            <span style={{ color: T.t2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {colLabel(col)}{col.tips && <KpiTooltip text={TIPS_TOOLTIP} isMobile />}
            </span>
            <span style={{ fontWeight: 500 }}><MoneyValue col={col} value={value} itemNet={itemNet} taxRmt={taxRmt} /></span>
        </div>
    );

    const cardStyle: React.CSSProperties = { background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 12, marginBottom: 10, overflow: 'hidden' };

    const ChannelCard = ({ row, label }: { row: ChannelRow; label: string }) => {
        const open = expanded.has(row.id);
        return (
            <div style={cardStyle}>
                <div onClick={() => toggle(row.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 14px', cursor: 'pointer' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <Caret open={open} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>{label}</span>
                    </span>
                    <span style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>{money(rowTotal(row, row.kind))}</div>
                        <div style={{ fontSize: 11, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{fmtNum(row.orders)} orders</div>
                    </span>
                </div>
                {open && (
                    <div style={{ background: T.bg, padding: '10px 14px 14px', borderTop: `1px solid ${T.divider}` }}>
                        {MONEY_COLS.map((col) => (
                            <FeeLine key={col.key} col={col} value={row.cells[col.key]} itemNet={row.itemTotalNet} taxRmt={row.taxRemitted} />
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 2px', marginTop: 4, borderTop: `1px solid ${T.divider}`, fontSize: 13.5, fontWeight: 700, color: T.t1 }}>
                            <span>Total Collected</span>
                            <span>{money(rowTotal(row, row.kind))}</span>
                        </div>
                        {row.subRows && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.divider}` }}>
                                {row.subRows.map((sub, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: T.t3 }}>
                                        <span>{sub.label} · {sub.orders}</span>
                                        <span style={{ color: T.t2, fontWeight: 600 }}>{money(rowTotal(sub, row.kind))}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {onChannelDrill && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onChannelDrill(row); }}
                                style={{ width: '100%', marginTop: 12, padding: '11px 0', border: `1px solid ${T.accent}`, borderRadius: 8, background: 'transparent', color: T.accent, fontSize: 13, fontWeight: 600, fontFamily: T.font, cursor: 'pointer' }}
                            >
                                View orders in Order Log {'›'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ position: 'relative' }}>
            {derived.groups.map((dg) => (
                <div key={dg.group.id}>
                    {!dg.single && (
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: T.t3, textTransform: 'uppercase', margin: '6px 2px 8px' }}>{dg.group.label}</div>
                    )}
                    {dg.visibleChannels.map((row) => (
                        <ChannelCard key={row.id} row={row} label={dg.single ? dg.group.label : row.label} />
                    ))}
                    {!dg.single && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.accentSoft, borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{dg.group.label} subtotal</span>
                            <span style={{ fontSize: 14, fontWeight: 800, color: T.t1 }}>{money(dg.subtotalTotal)}</span>
                        </div>
                    )}
                </div>
            ))}

            {/* Pinned grand-total bar */}
            <div style={{ position: 'sticky', bottom: 0, background: T.t1, borderRadius: 12, padding: '14px 16px', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, boxShadow: '0 -4px 16px rgba(0,0,0,0.12)' }}>
                <div style={{ minWidth: 0 }}>
                    {/* Forced two-line label — "Grand Total" / "Collected [icon]" — rather
                        than a width-driven wrap. Wrapping only by available space meant most
                        totals rendered on one line while a wide one (e.g. $8,669.59) wrapped,
                        an inconsistent look card-to-card. The `<br/>` makes the break the
                        same regardless of the amount's width; the nbsp between "Collected"
                        and the icon still keeps that pair glued together. */}
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.35, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase' }}>
                        Grand Total<br />
                        {'Collected '}
                        <span style={{ display: 'inline-block', verticalAlign: -1 }}>
                            <KpiTooltip text={GRAND_TOTAL_TOOLTIP} isMobile />
                        </span>
                    </div>
                    <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>
                        {fmtNum(derived.grandOrders)} orders
                        {derived.hasRmt && ` · tax rmt ${money(derived.grandTaxRmt)} · op ${money(derived.grandTaxOp)}`}
                    </div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>{money(derived.grandTotal)}</div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
//  EXPORT (EXP-2 outlet-scope modal: single vs all outlets, CSV / PDF / xlsx)
// ═══════════════════════════════════════════════════════════════════════════
type ExportFmt = 'xlsx' | 'csv' | 'pdf';
type OutletScope = 'single' | 'all';

/**
 * Column headings, built per export (not at module load) so the country-driven
 * "Service Fee" vs "Gratuity" wording is current.
 *
 * **No currency symbol here (SC-15).** It used to ride on the heading via
 * `moneyHeader` — `Item Total ($)` — which asserts one currency for the whole
 * sheet. That is wrong for the all-outlets export, where an Indian outlet's rows
 * (₹) and a US outlet's rows ($) share the same columns. The symbol now travels
 * with each row's values instead; see `ExportMatrix.currencyRows`.
 *
 * `neutralLabels` likewise drops the per-country column wording for the
 * all-outlets export: "Gratuity" is the US name for the Service Fee column, so
 * on a mixed-country sheet the neutral "Service Fee" is the only heading that
 * isn't wrong for somebody.
 */
const exportHeaders = (opts: { withOutlet?: boolean; neutralLabels?: boolean } = {}): string[] => [
    ...(opts.withOutlet ? ['Outlet'] : []),
    'Channel',
    'Orders',
    ...MONEY_COLS.map((c) => (opts.neutralLabels ? c.label : colLabel(c))),
    'Total Collected',
];

/**
 * Column indices for an export row. The all-outlets export prepends an Outlet
 * column, shifting everything right by one — so these are computed per export
 * rather than being module constants.
 *
 * `numeric` is every figure column (Orders plus the money columns), right-aligned
 * in the exports exactly as on screen so digits line up on the decimal point down
 * each column. Only the label columns stay left-aligned.
 */
function exportColIndexes(withOutlet: boolean) {
    const offset = withOutlet ? 1 : 0;
    const orders = offset + 1;
    const moneyStart = offset + 2;
    const money = [...MONEY_COLS.map((_, i) => moneyStart + i), moneyStart + MONEY_COLS.length];
    return { orders, money, numeric: [orders, ...money] };
}

/** Sums of 2dp inputs drift in binary floating point — pin them back to 2dp. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The Summary-by-Channel matrix plus the row indices the renderers shade.
 * Summary lines carry no marker of their own once flattened to a grid, so the
 * builder reports where they landed rather than making renderers re-detect them
 * by sniffing the label text.
 */
interface ExportMatrix {
    rows: (string | number)[][];
    /** Per-group subtotal rows. */
    subtotalRows: number[];
    /** Grand Total rows — one per outlet section (see `buildExportRows`). */
    totalRows: number[];
    /**
     * "No sales in this period" rows. Emphasised like a subtotal so a zero-trade
     * outlet reads as a reported result rather than a row that failed to fill in.
     */
    noSalesRows: number[];
    /**
     * Currency symbol per row index. Money VALUES are stored raw (no symbol) so
     * the xlsx keeps real number cells; each renderer applies the symbol its own
     * way — a currency number format for xlsx, a text prefix for CSV and PDF.
     */
    currencyRows: Record<number, string>;
    /** Column indices, which shift when the Outlet column is present. */
    cols: ReturnType<typeof exportColIndexes>;
    headers: string[];
}

/** One outlet's contribution to an export. */
interface ExportSection {
    /** Outlet name for the leading column — omitted entirely for a single-outlet export. */
    outlet?: string;
    /** Symbol resolved from THIS outlet's country, not the active one. */
    currency: string;
    derived: Derived;
}

/**
 * Build the Summary-by-Channel matrix (matches the on-screen table incl. group
 * headers, fulfillment sub-rows, subtotals, grand total and footnotes).
 *
 * Multiple sections = the all-outlets export: each outlet contributes its own
 * block ending in its OWN Grand Total. There is deliberately no total across
 * outlets — outlets can be in different currencies, so a combined figure would
 * be summing ₹ and $ into a meaningless number.
 */
function buildExportRows(sections: ExportSection[]): ExportMatrix {
    const withOutlet = sections.some((s) => s.outlet !== undefined);
    const cols = exportColIndexes(withOutlet);
    const headers = exportHeaders({ withOutlet, neutralLabels: withOutlet });
    const rows: (string | number)[][] = [];
    const subtotalRows: number[] = [];
    const totalRows: number[] = [];
    const noSalesRows: number[] = [];
    const currencyRows: Record<number, string> = {};
    const blankRow = () => headers.map(() => '');

    for (const section of sections) {
        const { derived, currency, outlet } = section;
        // Money cells are raw numbers (no symbol, no grouping) so xlsx/CSV stay
        // computable; '—' marks a component the platform does not report. The
        // net-of-promotion asterisk rides on the Channel label rather than the Item
        // Total value, which would otherwise turn that cell into text.
        const push = (row: (string | number)[]) => {
            if (currency) currencyRows[rows.length] = currency;
            rows.push(row);
        };
        const moneyRow = (label: string, orders: number | string, cells: FeeCells, total: number, itemNet = false) =>
            [
                ...(withOutlet ? [outlet ?? ''] : []),
                itemNet ? `${label} *` : label,
                orders,
                ...MONEY_COLS.map((col) => (cells[col.key] == null ? '—' : round2(cells[col.key] as number))),
                round2(total),
            ] as (string | number)[];

        // Outlet banner so each block is readable even though every row also
        // carries the outlet name in its own column.
        if (withOutlet) {
            const banner = blankRow();
            banner[0] = outlet ?? '';
            rows.push(banner);
        }

        // An outlet that traded nothing in this period still gets a line, so the
        // reader can tell "no sales" apart from an outlet that was left out.
        if (derived.isEmpty) {
            const none = blankRow();
            if (withOutlet) none[0] = outlet ?? '';
            none[withOutlet ? 1 : 0] = 'No sales in this period';
            noSalesRows.push(rows.length);
            rows.push(none);
            if (withOutlet) rows.push([]);
            continue;
        }

        for (const dg of derived.groups) {
            if (!dg.single) {
                const hdr = blankRow();
                // Outlet repeated on the group header too, so filtering the sheet by
                // Outlet returns that outlet's whole block rather than punching holes in it.
                if (withOutlet) hdr[0] = outlet ?? '';
                hdr[withOutlet ? 1 : 0] = dg.group.label;
                rows.push(hdr);
            }
            dg.visibleChannels.forEach((row) => {
                const label = dg.single ? dg.group.label : row.label;
                push(moneyRow(label, row.orders, row.cells, rowTotal(row, row.kind), !!row.itemTotalNet));
                // Fulfillment split (Delivery / Pickup / Dine-In) — indented under its channel.
                row.subRows?.forEach((sub) => {
                    push(moneyRow(`    ${sub.label}`, sub.orders, sub.cells, rowTotal(sub, row.kind), !!row.itemTotalNet));
                });
            });
            if (!dg.single) {
                subtotalRows.push(rows.length);
                push(moneyRow(`${dg.group.label} subtotal`, dg.subtotalOrders, dg.subtotalCells, dg.subtotalTotal, dg.visibleChannels.some((c) => c.itemTotalNet)));
            }
        }
        // Just "Grand Total" — the Outlet column on the same row already names it,
        // so "Chicago | Chicago — Grand Total" would say it twice.
        totalRows.push(rows.length);
        push(moneyRow('Grand Total', derived.grandOrders, derived.grandCells, derived.grandTotal));
        if (withOutlet) rows.push([]);
    }

    // ── Footnotes ──
    const hasNet = sections.some((s) => s.derived.hasNet);
    const rmtSections = sections.filter((s) => s.derived.hasRmt);
    if (!withOutlet) rows.push([]);
    if (hasNet) rows.push([`* Marketplace rows: item totals are net of platform promotions (promotion data not reported by the platform).`]);
    rows.push([`Tips = restaurant-retained tips only; driver tips excluded.`]);
    // One tax line per outlet that reports remitted tax — a single combined line
    // would have to pick one currency for figures drawn from several.
    rmtSections.forEach((s) => {
        const label = s.outlet ? `Tax (${s.outlet})` : 'Tax';
        rows.push([`${label}: remitted (RMT) ${moneyWithSymbol(s.derived.grandTaxRmt, s.currency)} · operator ${moneyWithSymbol(s.derived.grandTaxOp, s.currency)}.`]);
    });
    rows.push([`Total Collected = amount paid by guests; not the operator's expected deposit (marketplace fees retained by platform).`]);
    if (withOutlet) rows.push([`Each outlet is totalled in its own currency; no combined total is shown across outlets.`]);
    return { rows, subtotalRows, totalRows, noSalesRows, currencyRows, cols, headers };
}

function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

const SHEET_TITLE = 'Summary by Channel';

/**
 * The KPI's on-screen name. Single source of truth for the card heading, the PDF
 * heading and the exported file name — so a rename can never leave the download
 * called something the user has never seen on screen.
 */
export const KPI_TITLE = 'Sales by Channel — Financial Breakdown';

/** "Sales by Channel — Financial Breakdown" → "sales-by-channel-financial-breakdown". */
const slugify = (s: string): string =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/**
 * Render one matrix cell as display text, with its row's currency symbol on the
 * money columns. Used by both PDF paths and by CSV.
 *
 * `grouped` is the only difference between them: a PDF is read by a human, so
 * money gets thousands separators; a CSV is opened in a spreadsheet, where an
 * embedded comma would need quoting and hurts the odds of the cell being parsed
 * back as a number, so it stays ungrouped. The xlsx path never comes here — it
 * keeps the raw number and gets its symbol from a currency number format.
 */
function displayCell(value: string | number, colIdx: number, matrix: ExportMatrix, rowIdx: number, grouped: boolean): string {
    if (typeof value === 'number' && matrix.cols.money.includes(colIdx)) {
        return moneyWithSymbol(value, matrix.currencyRows[rowIdx] ?? '', grouped);
    }
    return String(value ?? '');
}

async function runExport(sections: ExportSection[], fmt: ExportFmt, scope: OutletScope, dateRangeLabel?: string) {
    const matrix = buildExportRows(sections);
    const { rows, headers } = matrix;
    // Shared by every format: subtotals get the light tint, Grand Totals the strong
    // one, and the figure columns are right-aligned. `decimalColumns` /
    // `currencyRows` are xlsx-only — the PDF exporter ignores the extra keys.
    //
    // "No sales in this period" rides the subtotal emphasis rather than earning a
    // key of its own: the shared xlsx and native-PDF exporters have divergent
    // lineages across the web/native branch pairs, so a new layout key would need
    // porting into both of them four times to show one shaded row.
    const layout = {
        subtotal: [...matrix.subtotalRows, ...matrix.noSalesRows],
        total: matrix.totalRows,
        decimalColumns: matrix.cols.money,
        alignRight: matrix.cols.numeric,
        currencyRows: matrix.currencyRows,
    };
    // Same two facts, in the same wording, that every other Reports export states
    // (DPFIX-07): CSV and xlsx take them as leading rows, the PDFs as a subtitle.
    const meta: (string | number)[][] | undefined = dateRangeLabel
        ? [['Date range', dateRangeLabel], ['Generated', generatedAtLabel()]]
        : undefined;
    const subtitle = dateRangeLabel
        ? `${SHEET_TITLE} · ${dateRangeLabel} · Exported on ${exportedOnLabel()}`
        : `${SHEET_TITLE} · Exported on ${exportedOnLabel()}`;
    // Named after the KPI as it appears on screen. Only the multi-outlet case is
    // tagged — "-outlet" on every single-outlet download was noise.
    const base = `${slugify(KPI_TITLE)}${scope === 'all' ? '-all-outlets' : ''}`;

    if (fmt === 'pdf') {
        const title = KPI_TITLE;
        if (isNativePlatform()) {
            // Explicit indices, not "last row" — the matrix ends in footnotes, not the total.
            const text = rows.map((r, ri) => r.map((c, ci) => displayCell(c, ci, matrix, ri, true)));
            await exportTablePdfNative(title, subtitle, headers, text, `${base}.pdf`, true, undefined, layout);
            return;
        }
        openChannelPdf(title, base, matrix, subtitle);
        return;
    }

    if (fmt === 'csv') {
        if (isNativePlatform()) {
            await exportTableXlsxNative(headers, rows, `${base}.xlsx`, SHEET_TITLE, meta, undefined, layout);
            return;
        }
        // Money cells now carry their row's currency symbol, so they are text.
        // This branch's escapeCsvCell has no `numeric` parameter (no right-align /
        // SUM hint for Excel), so every cell — Orders included — is escaped the
        // same way regardless of column.
        const text = rows.map((r, ri) => r.map((c, ci) => displayCell(c, ci, matrix, ri, false)));
        // Meta first, then a blank line, then the table — the same shape `buildCsv`
        // gives every other Reports CSV, so the header row stays the first line a
        // parser meets after the preamble.
        const metaLines = meta?.length
            ? [...meta.map((r) => r.map((c) => escapeCsvCell(c)).join(',')), '']
            : [];
        const csv = `${CSV_BOM}${[
            ...metaLines,
            [
                headers.map((h) => escapeCsvCell(h)),
                ...text.map((r) => r.map((c) => escapeCsvCell(c))),
            ].map((r) => r.join(',')).join('\r\n'),
        ].join('\r\n')}`;
        triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${base}.csv`);
        return;
    }

    // xlsx (default)
    if (isNativePlatform()) {
        await exportTableXlsxNative(headers, rows, `${base}.xlsx`, SHEET_TITLE, meta, undefined, layout);
        return;
    }
    const { buildXlsx, xlsxFilename } = await import('../../utils/xlsxExport');
    const bytes = buildXlsx(headers, rows, SHEET_TITLE, meta, undefined, layout);
    triggerDownload(new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), xlsxFilename(base));
}

function openChannelPdf(title: string, filename: string, matrix: ExportMatrix, subtitle: string) {
    const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const th = 'style="border:1px solid #E2E5EC;padding:7px 10px;text-align:right;font-size:10px;font-weight:600;color:#475569;background:#F5F6FA;text-transform:uppercase"';
    const td = 'style="border:1px solid #E2E5EC;padding:7px 10px;font-size:11px;color:#1A1D2E;text-align:right"';
    const tdL = 'style="border:1px solid #E2E5EC;padding:7px 10px;font-size:11px;color:#1A1D2E;text-align:left;white-space:pre"';
    const { headers } = matrix;
    const ths = headers.map((h, i) => `<th ${i === 0 ? th.replace('right', 'left') : th}>${esc(h)}</th>`).join('');
    // Subtle tints so subtotals and the grand total read as summary lines.
    // `-webkit-print-color-adjust` keeps them from being dropped by print preview.
    const KEEP_BG = '-webkit-print-color-adjust:exact;print-color-adjust:exact';
    const subtotalSet = new Set(matrix.subtotalRows);
    const totalSet = new Set(matrix.totalRows);
    // Shaded and bold so a zero-trade outlet reads as a reported result rather than
    // a row that failed to fill in — it is the one line in its block carrying no figures.
    const noSalesSet = new Set(matrix.noSalesRows);
    const labelCols = matrix.cols.orders - 1; // Outlet (when present) + Channel
    const trs = matrix.rows.map((r, ri) => {
        if (r.length <= 1) return r.length ? `<tr><td ${tdL} colspan="${headers.length}" style="border:1px solid #E2E5EC;padding:6px 10px;font-size:10px;color:#64748B;text-align:left">${esc(r[0])}</td></tr>` : '';
        const isTotal = totalSet.has(ri);
        const isSubtotal = subtotalSet.has(ri);
        const isNoSales = noSalesSet.has(ri);
        const rowStyle = isTotal
            ? ` style="background:#E9ECF4;font-weight:700;${KEEP_BG}"`
            : isNoSales
            ? ` style="background:#F6F7FB;font-weight:700;${KEEP_BG}"`
            : isSubtotal
            ? ` style="background:#F6F7FB;font-weight:600;${KEEP_BG}"`
            : '';
        return `<tr${rowStyle}>${r.map((c, i) => `<td ${i < labelCols ? tdL : td}>${esc(displayCell(c, i, matrix, ri, true))}</td>`).join('')}</tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(filename)}</title>
      <style>body{font-family:'DM Sans',sans-serif;padding:36px;color:#1A1D2E}h1{font-size:17px;margin:0 0 4px}.sub{font-size:11px;color:#475569;margin-bottom:16px}table{width:100%;border-collapse:collapse}@media print{button{display:none}}</style>
      </head><body><h1>${esc(title)}</h1><div class="sub">${esc(subtitle)}</div>
      <button onclick="window.print()" style="padding:8px 18px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;margin-bottom:14px">Print / Save as PDF</button>
      <table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table></body></html>`;
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
}

/**
 * Date window the export re-fetches on. Mirrors what `useSalesByChannelBreakdown`
 * was called with on screen, so an all-outlets export covers exactly the period
 * the user is looking at.
 */
export interface BreakdownDateRange {
    startDate?: string;
    endDate?: string;
    /** Today omits the dates entirely (the live path), matching the on-screen hook. */
    isToday?: boolean;
    /** Global sales-channel filter selection, matching the on-screen hook. Omitted for Overall. */
    orderSource?: OrderSource;
}

/** Dig an access token out of the auth blob (same shape-tolerant walk the contexts use). */
function findAuthToken(obj: any, depth = 0): string | null {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findAuthToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
}


/**
 * Fetch and map the breakdown for every outlet, one section each.
 *
 * Sequential rather than `Promise.all`: this is an on-demand export against a
 * ClickHouse reporting endpoint, and firing N concurrent requests at it for a
 * large estate is the kind of thing that trips rate limits. Outlets that fail or
 * have no data are skipped rather than aborting the whole export — a partial
 * workbook beats none, and an outlet with no sales has nothing to contribute.
 */
async function fetchAllOutletSections(
    outlets: AccessibleOutlet[],
    dateRange: BreakdownDateRange | undefined,
    token: string | null,
    onProgress?: (done: number, total: number) => void,
): Promise<ExportSection[]> {
    const sections: ExportSection[] = [];
    for (let i = 0; i < outlets.length; i += 1) {
        const outlet = outlets[i];
        try {
            const res = await getSalesByChannelBreakdownApi({
                locationId: outlet.id,
                ...(dateRange?.isToday ? {} : { startDate: dateRange?.startDate, endDate: dateRange?.endDate }),
                orderSource: dateRange?.orderSource,
                token,
            });
            const { groups, totals } = mapSalesByChannelBreakdown(res);
            // Outlets with no sales are KEPT (they render a "no sales" line). Dropping
            // them made the export look like it had missed outlets — the reader cannot
            // tell "no trade" from "never fetched" when the row simply isn't there.
            sections.push({
                outlet: outlet.name,
                currency: getCurrencySymbolForCountry(outlet.country),
                derived: deriveModel(groups, totals),
            });
        } catch {
            // Only a genuine fetch failure is skipped — see the note above.
        }
        onProgress?.(i + 1, outlets.length);
    }
    return sections;
}

/**
 * Export dialog for this KPI.
 *
 * SC-16: this used to be a hand-rolled dialog that had drifted from the shared
 * `ExportScopeModal` — no Cancel button, a full-width Export bar, and its own
 * section-label and radio-card metrics. It now renders the shared dialog itself,
 * so the two can no longer diverge; only the scope/format vocabulary is mapped
 * (`'outlet'` → `'single'`, `'excel'` → `'xlsx'`) onto this card's pipeline.
 */
function ExportModal({ derived, outletName, dateRange, dateRangeLabel, outlets, onClose }: { derived: Derived; outletName?: string; dateRange?: BreakdownDateRange; dateRangeLabel?: string; outlets: AccessibleOutlet[]; onClose: () => void }) {
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { auth } = useAuth();

    const doExport = async (pickedScope: ExportScope, pickedFormat: ExportFormat) => {
        const scope: OutletScope = pickedScope === 'all' ? 'all' : 'single';
        const fmt: ExportFmt = pickedFormat === 'excel' ? 'xlsx' : pickedFormat;
        setBusy(true);
        setError(null);
        try {
            if (scope === 'all') {
                const token = findAuthToken(auth);
                if (!outlets.length) {
                    setError('Could not load the outlet list. Try again, or export this outlet only.');
                    return;
                }
                const sections = await fetchAllOutletSections(
                    outlets,
                    dateRange,
                    token,
                    (done, total) => setProgress(`Fetching outlet ${done} of ${total}…`),
                );
                // Empty outlets are kept as "no sales" lines, so an empty list here
                // means every request failed — not that nobody traded.
                if (!sections.length) {
                    setError('Could not load data for any outlet. Please try again.');
                    return;
                }
                setProgress('Building file…');
                await runExport(sections, fmt, scope, dateRangeLabel);
            } else {
                // Single outlet: the on-screen model is already loaded — no refetch.
                await runExport([{ currency: getCurrencySymbol(), derived }], fmt, scope, dateRangeLabel);
            }
            onClose();
        } catch {
            setError('Export failed. Please try again.');
        } finally {
            setBusy(false);
            setProgress(null);
        }
    };

    return (
        <ExportScopeModal
            title="Sales by Channel"
            open
            onClose={() => { if (!busy) onClose(); }}
            currentOutletName={outletName ?? 'Current outlet only'}
            outletCount={outlets.length}
            onConfirm={(scope, format) => void doExport(scope, format)}
            loading={busy}
            progressLabel={progress ?? undefined}
            error={error}
        />
    );
}

// ─── Loading shimmer (table on desktop, cards on mobile) ───────────────────
function BreakdownShimmer({ isDesktop }: { isDesktop: boolean }) {
    if (isDesktop) {
        return (
            <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    {MONEY_COLS.map((c, i) => <Bone key={c.key} w={i === 0 ? 120 : 80} h={10} />)}
                </div>
                {[0, 1, 2, 3, 4].map((r) => (
                    <div key={r} style={{ display: 'flex', gap: 12, padding: '10px 0', borderTop: `1px solid ${T.divider}` }}>
                        {MONEY_COLS.map((c, i) => <Bone key={c.key} w={i === 0 ? 120 : 80} h={13} />)}
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div>
            {[0, 1, 2].map((r) => (
                <div key={r} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: '14px', marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                    <Bone w={120} h={15} />
                    <Bone w={80} h={15} />
                </div>
            ))}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PUBLIC COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export interface SalesByChannelBreakdownProps {
    /** Groups mapped from the API via `mapSalesByChannelBreakdown()`. */
    data?: ChannelGroup[];
    /** Grand-total row from the API `totals` object. Falls back to the aggregate. */
    totals?: ChannelGrandTotals;
    loading?: boolean;
    error?: boolean;
    onRetry?: () => void;
    /** Row click → Order Log pre-filtered to the channel (Sales Report only). */
    onChannelDrill?: (channel: ChannelRow) => void;
    /** Shown in the export modal's "This outlet" option. */
    outletName?: string;
    /**
     * The period on screen. Required for the "All outlets" export, which refetches
     * every outlet over this same window — without it that export silently covers
     * Today regardless of the selected filter.
     */
    dateRange?: BreakdownDateRange;
    /**
     * Human-readable period for the export header, e.g. "Aug 17, 2026 – Aug 19, 2026".
     * Comes from the screen's own `exportPeriodFor(...)` label so this KPI's download
     * states the period in the same words as every other export on the same screen.
     */
    dateRangeLabel?: string;
    style?: React.CSSProperties;
}

const EMPTY_GROUPS: ChannelGroup[] = [];

export default function SalesByChannelBreakdown({ data = EMPTY_GROUPS, totals, loading, error, onRetry, onChannelDrill, outletName, dateRange, dateRangeLabel, style }: SalesByChannelBreakdownProps) {
    const isDesktop = useIsDesktop();
    const derived = useDerived(data, totals);
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const canExport = !loading && !error && !derived.isEmpty;

    // Only a user who can actually reach more than one outlet has a scope to
    // choose; everyone else gets the plain format menu the rest of Reports uses.
    // `useOutletAccess` gates on the same super-role check as the sidebar's outlet
    // switcher, so a single-location manager never sees the dialog even though the
    // details payload lists the merchant's whole estate.
    const { outlets, isMultiOutlet } = useOutletAccess();

    // Single-outlet export — no refetch, the on-screen model is already loaded.
    const exportThisOutlet = async (fmt: ExportFmt) => {
        if (exporting) return;
        setExporting(true);
        try { await runExport([{ currency: getCurrencySymbol(), derived }], fmt, 'single', dateRangeLabel); }
        catch { /* swallow — a failed export must not take the screen down */ }
        finally { setExporting(false); }
    };

    return (
        <div className="rpt-card" style={{ marginBottom: 24, ...style }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                <div>
                    <div style={{ fontSize: isDesktop ? 16 : 14, fontWeight: 600, color: T.t1 }}>{KPI_TITLE}</div>
                    <div style={{ fontSize: isDesktop ? 12 : 11, color: T.t3, marginTop: 2 }}>What each channel contributed and what the guest paid</div>
                </div>
                {canExport && (isMultiOutlet ? (
                    // Multi-outlet: the trigger opens this KPI's outlet-scope dialog. Same
                    // button chrome as every other Reports export so the two read as one
                    // control family; only the click behaviour differs.
                    <ReportExportTriggerButton onClick={() => setExportOpen(true)} />
                ) : (
                    // Single outlet: no scope to pick, so skip the dialog entirely and offer
                    // the formats directly, exactly like the other sections' export buttons.
                    <ReportExportOptionsMenu
                        loading={exporting}
                        options={[
                            { label: 'Export as CSV', icon: 'table_chart', iconColor: T.green, iconClass: 'eod-mobile-export-sheet__icon--csv', description: 'Spreadsheet-ready format', onSelect: () => void exportThisOutlet('csv') },
                            { label: 'Export as Excel', icon: 'grid_on', iconColor: T.green, iconClass: 'eod-mobile-export-sheet__icon--excel', description: 'Excel workbook', onSelect: () => void exportThisOutlet('xlsx') },
                            { label: 'Export as PDF', icon: 'picture_as_pdf', iconColor: T.accent, iconClass: 'eod-mobile-export-sheet__icon--pdf', description: 'Print-ready document', onSelect: () => void exportThisOutlet('pdf') },
                        ]}
                    />
                ))}
            </div>

            {loading ? (
                <BreakdownShimmer isDesktop={isDesktop} />
            ) : error ? (
                <SectionError kpiName="sales by channel breakdown" onRetry={onRetry} size="medium" minHeight={200} />
            ) : derived.isEmpty ? (
                <SectionEmpty kpiName="sales by channel breakdown" minHeight={200} />
            ) : isDesktop ? (
                <DesktopTable derived={derived} onChannelDrill={onChannelDrill} />
            ) : (
                <MobileCards derived={derived} onChannelDrill={onChannelDrill} />
            )}

            {exportOpen && <ExportModal derived={derived} outletName={outletName} dateRange={dateRange} dateRangeLabel={dateRangeLabel} outlets={outlets} onClose={() => setExportOpen(false)} />}
        </div>
    );
}
